import React, { useEffect, useRef } from "react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

/**
 * Auto-logout after IDLE_MINUTES of no user activity.
 * HIPAA 45 CFR §164.312(a)(2)(iii) — automatic logoff.
 * Activity events: mousemove, mousedown, keydown, touchstart, scroll.
 * Shows a warning 60s before forced logout.
 */
const IDLE_MINUTES = 15;
const WARN_BEFORE_S = 60;

export default function IdleTimeout() {
  const { user, logout } = useAuth();
  const lastActivity = useRef(Date.now());
  const warnedRef = useRef(false);

  useEffect(() => {
    if (!user) return;

    const reset = () => {
      lastActivity.current = Date.now();
      warnedRef.current = false;
    };

    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));

    const tick = setInterval(() => {
      const idleMs = Date.now() - lastActivity.current;
      const idleS = Math.floor(idleMs / 1000);
      const limitS = IDLE_MINUTES * 60;
      if (idleS >= limitS) {
        toast.error("Signed out due to inactivity (HIPAA auto-logoff).");
        logout();
        return;
      }
      if (!warnedRef.current && idleS >= limitS - WARN_BEFORE_S) {
        warnedRef.current = true;
        toast.warning(`You will be signed out in ${WARN_BEFORE_S} seconds due to inactivity.`, {
          duration: WARN_BEFORE_S * 1000,
        });
      }
    }, 5000);

    return () => {
      events.forEach((e) => window.removeEventListener(e, reset));
      clearInterval(tick);
    };
  }, [user, logout]);

  return null;
}
